import React, {
  useCallback, useMemo, useState,
} from 'react';

import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { Button, TextField, InputLabel } from '@picsio/ui';

import store from '../../../store';
import { create } from '../../../store/products/create';
import { setSelectedProductChannel } from '../../../store/products/setSelectedProductChannel';
import localization from '../../../shared/strings';
import { decodeSlash } from '../../../shared/utils';
import { navigate } from '../../../helpers/history';
import Logger from '../../../services/Logger';
import UiBlocker from '../../../services/UiBlocker';
import Icon from '../../Icon';
import { PRODUCT_FIELD_MAX_LENGTH, productFieldInputProps } from '../../ProductInfo/constants';
import ProductStatusSelect from '../../ProductInfo/ProductStatusSelect';
import Tooltip from '../../Tooltip';
import Toast from '../../Toast';
import styles from './styles.module.scss';

const normalizeName = (name) => decodeSlash(name.trim().toLowerCase());

const strings = localization.DIALOGS.PRODUCT_DIALOGS.CREATE_MASTER_PRODUCT;

const getStatusSelectMenuProps = () => ({
  PopperProps: {
    portalContainer: document.querySelector('.wrapperDialog') || document.body,
  },
});

function CreateMasterProductModalBody({
  root,
  onSuccess,
}) {
  const dispatch = useDispatch();
  const products = useSelector((state) => state.products.items);

  const [title, setTitle] = useState('');
  const [sku, setSku] = useState('');
  const [status, setStatus] = useState('DRAFT');
  const [titleError, setTitleError] = useState('');
  const [skuError, setSkuError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const titleDuplicateWarning = useMemo(() => {
    const trimmed = title.trim();
    if (!trimmed) return '';

    const exists = products.some(
      (product) => normalizeName(product.title) === normalizeName(trimmed),
    );

    return exists ? strings.TITLE_DUPLICATE_WARNING : '';
  }, [products, title]);

  const titleMaxLengthWarning = useMemo(() => (
    title.length >= PRODUCT_FIELD_MAX_LENGTH
      ? strings.TITLE_MAX_LENGTH(PRODUCT_FIELD_MAX_LENGTH)
      : ''
  ), [title]);

  const destroy = useCallback(() => {
    try {
      root.unmount();
    } catch { /* ignore */ }
  }, [root]);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    destroy();
  }, [destroy, isSubmitting]);

  const validate = useCallback(() => {
    let valid = true;
    const trimmedTitle = title.trim();
    const trimmedSku = sku.trim();

    if (!trimmedTitle) {
      setTitleError(strings.TITLE_REQUIRED);
      valid = false;
    } else {
      setTitleError('');
    }

    if (!trimmedSku) {
      setSkuError(strings.SKU_REQUIRED);
      valid = false;
    } else if (skuError === strings.SKU_REQUIRED) {
      setSkuError('');
    }

    return valid;
  }, [sku, skuError, title]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    if (!validate()) return;

    const trimmedTitle = title.trim();
    const trimmedSku = sku.trim();

    Logger.log('User', 'ProductsPanelAddProductConfirm');

    setIsSubmitting(true);
    setSkuError('');
    UiBlocker.block(`Creating product ${trimmedTitle}`);

    try {
      const result = await dispatch(create({
        title: trimmedTitle,
        sku: trimmedSku,
        status,
      }));

      if (create.fulfilled.match(result)) {
        const productId = result.payload._id;
        dispatch(setSelectedProductChannel({ productId, channelId: 'master' }));
        destroy();
        navigate(`/product/${productId}`);
        Toast(strings.SUCCESS_TOAST, { type: 'success' });
        onSuccess?.();
        return;
      }

      if (create.rejected.match(result) && result.payload?.code === 'DUPLICATE_SKU') {
        setSkuError(result.payload.message || strings.SKU_DUPLICATE);
      }
    } finally {
      UiBlocker.unblock();
      setIsSubmitting(false);
    }
  }, [
    dispatch,
    destroy,
    isSubmitting,
    onSuccess,
    sku,
    status,
    title,
    validate,
  ]);

  const handleTitleChange = useCallback((event) => {
    setTitle(event.target.value);
    if (titleError) setTitleError('');
  }, [titleError]);

  const handleSkuChange = useCallback((event) => {
    setSku(event.target.value);
    if (skuError) setSkuError('');
  }, [skuError]);

  const handleStatusChange = useCallback((apiStatus) => {
    setStatus(apiStatus || 'DRAFT');
  }, []);

  const isMissingRequiredFields = !title.trim() || !sku.trim();
  const isTitleTooLong = title.length > PRODUCT_FIELD_MAX_LENGTH;
  const isCreateDisabled = isSubmitting || isMissingRequiredFields || isTitleTooLong;
  const titleHelperText = titleError || titleDuplicateWarning || titleMaxLengthWarning;
  const createDisabledTooltip = isMissingRequiredFields && !isSubmitting
    ? strings.CREATE_DISABLED_TOOLTIP
    : (isTitleTooLong && !isSubmitting ? titleMaxLengthWarning : null);

  return (
    <div className={cn('simpleDialog', styles.createMasterProductModal)}>
      <div className="simpleDialogUnderlayer" />
      <div className="simpleDialogBox">
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">{strings.TITLE}</span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            onClick={handleClose}
            onKeyDown={handleClose}
          >
            <Icon name="close" />
          </span>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <div className={styles.form}>
              <TextField
                value={title}
                label={strings.TITLE_LABEL}
                placeholder={strings.TITLE_PLACEHOLDER}
                error={Boolean(titleError) || Boolean(titleDuplicateWarning) || Boolean(titleMaxLengthWarning)}
                helperText={titleHelperText}
                disabled={isSubmitting}
                inputProps={productFieldInputProps}
                onChange={handleTitleChange}
              />
              <div className={styles.skuStatusRow}>
                <div className={styles.skuField}>
                  <TextField
                    value={sku}
                    label={strings.SKU_LABEL}
                    placeholder={strings.SKU_PLACEHOLDER}
                    error={Boolean(skuError)}
                    helperText={skuError}
                    disabled={isSubmitting}
                    inputProps={productFieldInputProps}
                    onChange={handleSkuChange}
                  />
                </div>
                <div className={styles.statusField}>
                  <InputLabel position="top" margin>
                    {strings.STATUS_LABEL}
                  </InputLabel>
                  <ProductStatusSelect
                    includeArchived
                    productStatus={status}
                    disabled={isSubmitting}
                    menuProps={getStatusSelectMenuProps()}
                    onChange={handleStatusChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="simpleDialogFooter">
          <div className={styles.footer}>
            <Button
              color="secondary"
              size="md"
              variant="contained"
              disabled={isSubmitting}
              onClick={handleClose}
            >
              {strings.BTN_CANCEL}
            </Button>
            <Tooltip content={createDisabledTooltip} placement="top">
              <span className={styles.createButtonWrap}>
                <Button
                  variant="contained"
                  color="primary"
                  size="md"
                  disabled={isCreateDisabled}
                  onClick={handleSubmit}
                >
                  {strings.BTN_CREATE}
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}

CreateMasterProductModalBody.propTypes = {
  root: PropTypes.shape({ unmount: PropTypes.func.isRequired }).isRequired,
  onSuccess: PropTypes.func,
};

CreateMasterProductModalBody.defaultProps = {
  onSuccess: null,
};

function CreateMasterProductModal(props) {
  return (
    <Provider store={store}>
      <CreateMasterProductModalBody {...props} />
    </Provider>
  );
}

export function showCreateMasterProductModal({ onSuccess } = {}) {
  const $wrapper = document.querySelector('.wrapperDialog');
  const root = createRoot($wrapper);
  root.render(
    <CreateMasterProductModal
      root={root}
      onSuccess={onSuccess}
    />,
  );
}

export default CreateMasterProductModal;
